'use client'

import * as React from 'react'
import { CountrySelector, CountrySelectorProps, Country } from './CountrySelector'
import { COUNTRIES } from '../utils/countries'

export interface CitizenshipSelectorProps
  extends Omit<CountrySelectorProps, 'multiple' | 'value' | 'onValueChange'> {
  value?: string[]
  onValueChange: (value: string[]) => void
  maxCitizenships?: number
  allowDualCitizenship?: boolean
  suggestResidenceCountry?: string
  helperText?: string
}

/**
 * CitizenshipSelector Component
 *
 * A specialized country selector for managing multiple citizenships.
 * Built on top of CountrySelector with citizenship-specific features.
 *
 * Features:
 * - Multiple citizenship selection (always enabled)
 * - Maximum citizenship limits
 * - Residence country suggestions
 * - Dual citizenship validation
 * - Specialized placeholder and helper text
 *
 * @example
 * ```tsx
 * <CitizenshipSelector
 *   value={['CH', 'US']}
 *   onValueChange={setCitizenships}
 *   maxCitizenships={3}
 *   suggestResidenceCountry="CH"
 *   helperText="Select all countries where you hold citizenship"
 * />
 * ```
 */
export function CitizenshipSelector({
  value = [],
  onValueChange,
  maxCitizenships = 5,
  allowDualCitizenship = true,
  suggestResidenceCountry,
  helperText = 'Select all countries where you hold citizenship or passport',
  placeholder = 'Select your citizenships...',
  searchPlaceholder = 'Search countries...',
  disabled = false,
  className,
  maxSelectedDisplay = 3,
  countries = COUNTRIES,
  showFlags = true,
  showCodes = true,
  size = 'md',
  ...props
}: CitizenshipSelectorProps) {
  // Validate and enforce maximum citizenships limit
  const handleValueChange = React.useCallback(
    (newValue: string | string[]) => {
      const newCitizenships = Array.isArray(newValue) ? newValue : [newValue]

      // Enforce maximum citizenships limit
      if (newCitizenships.length > maxCitizenships) {
        // Show warning or handle overflow
        console.warn(`Maximum ${maxCitizenships} citizenships allowed`)
        return
      }

      // Validate dual citizenship if not allowed
      if (!allowDualCitizenship && newCitizenships.length > 1) {
        console.warn('Dual citizenship is not allowed in this context')
        return
      }

      onValueChange(newCitizenships)
    },
    [onValueChange, maxCitizenships, allowDualCitizenship]
  )

  // Enhanced placeholder based on constraints
  const dynamicPlaceholder = React.useMemo(() => {
    if (!allowDualCitizenship) {
      return 'Select your primary citizenship...'
    }

    if (maxCitizenships === 1) {
      return 'Select your citizenship...'
    }

    const remainingSlots = maxCitizenships - value.length
    if (remainingSlots <= 1 && value.length > 0) {
      return `Select up to ${remainingSlots} more citizenship${remainingSlots === 1 ? '' : 's'}...`
    }

    return placeholder
  }, [allowDualCitizenship, maxCitizenships, value.length, placeholder])

  // Enhanced helper text with context
  const enhancedHelperText = React.useMemo(() => {
    let text = helperText

    if (suggestResidenceCountry && !value.includes(suggestResidenceCountry)) {
      const residenceCountry = countries.find(c => c.code === suggestResidenceCountry)
      if (residenceCountry) {
        text += ` Consider adding ${residenceCountry.flag} ${residenceCountry.name} if you hold citizenship there.`
      }
    }

    if (!allowDualCitizenship) {
      text += ' Note: Only single citizenship is allowed.'
    } else if (maxCitizenships < 5) {
      text += ` Maximum ${maxCitizenships} citizenships allowed.`
    }

    return text
  }, [helperText, suggestResidenceCountry, value, countries, allowDualCitizenship, maxCitizenships])

  return (
    <div className="space-y-2">
      <CountrySelector
        value={value}
        onValueChange={handleValueChange}
        multiple={true}
        placeholder={dynamicPlaceholder}
        searchPlaceholder={searchPlaceholder}
        disabled={disabled}
        className={className}
        maxSelectedDisplay={maxSelectedDisplay}
        countries={countries}
        showFlags={showFlags}
        showCodes={showCodes}
        size={size}
        {...props}
      />

      {enhancedHelperText && (
        <p className="text-sm text-muted-foreground">
          {enhancedHelperText}
        </p>
      )}

      {/* Citizenship count indicator */}
      {value.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {value.length} citizenship{value.length === 1 ? '' : 's'} selected
          </span>
          {maxCitizenships < Infinity && (
            <span>
              {maxCitizenships - value.length} remaining
            </span>
          )}
        </div>
      )}

      {/* Warning for dual citizenship restrictions */}
      {!allowDualCitizenship && value.length > 1 && (
        <p className="text-sm text-amber-600 font-medium">
          ⚠️ Dual citizenship is not permitted. Please select only one citizenship.
        </p>
      )}
    </div>
  )
}

/**
 * Hook for managing citizenship state with validation
 */
export function useCitizenshipSelector({
  initialCitizenships = [],
  maxCitizenships = 5,
  allowDualCitizenship = true,
  residenceCountry,
}: {
  initialCitizenships?: string[]
  maxCitizenships?: number
  allowDualCitizenship?: boolean
  residenceCountry?: string
} = {}) {
  const [citizenships, setCitizenships] = React.useState<string[]>(initialCitizenships)
  const [errors, setErrors] = React.useState<string[]>([])

  const addCitizenship = React.useCallback(
    (countryCode: string) => {
      setCitizenships(prev => {
        if (prev.includes(countryCode)) return prev

        const newCitizenships = [...prev, countryCode]

        // Validate constraints
        const newErrors: string[] = []

        if (newCitizenships.length > maxCitizenships) {
          newErrors.push(`Maximum ${maxCitizenships} citizenships allowed`)
          return prev
        }

        if (!allowDualCitizenship && newCitizenships.length > 1) {
          newErrors.push('Dual citizenship is not allowed')
          return prev
        }

        setErrors(newErrors)
        return newCitizenships
      })
    },
    [maxCitizenships, allowDualCitizenship]
  )

  const removeCitizenship = React.useCallback((countryCode: string) => {
    setCitizenships(prev => prev.filter(code => code !== countryCode))
    setErrors([])
  }, [])

  const hasMaxCitizenships = citizenships.length >= maxCitizenships
  const hasDualCitizenship = citizenships.length > 1
  const canAddMore = !hasMaxCitizenships && (allowDualCitizenship || citizenships.length === 0)

  // Auto-suggest residence country as citizenship if not already added
  const suggestions = React.useMemo(() => {
    const suggestions: string[] = []

    if (residenceCountry && !citizenships.includes(residenceCountry)) {
      suggestions.push(residenceCountry)
    }

    return suggestions
  }, [residenceCountry, citizenships])

  return {
    citizenships,
    setCitizenships,
    addCitizenship,
    removeCitizenship,
    errors,
    hasMaxCitizenships,
    hasDualCitizenship,
    canAddMore,
    suggestions,
  }
}